// Webmail integrata — casella IMAP/SMTP personale (Posta in arrivo, lettura, scrivi/rispondi/inoltra, allegati)
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Inbox, Send, Trash2, FileText, AlertOctagon, Folder, RefreshCw, Search, PenSquare,
  Paperclip, Reply, ReplyAll, Forward, MailOpen, Mail as MailIcon, ChevronLeft, ChevronRight,
  Loader2, X, Download, Settings, ArrowLeft,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { RecipientInput, DocPicker } from "./WebmailComposeExtras";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_ICONS = { inbox: Inbox, sent: Send, drafts: FileText, trash: Trash2, spam: AlertOctagon };
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n > 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);
const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const today = new Date();
  if (dt.toDateString() === today.toDateString()) return dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  return dt.toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) + (dt.getFullYear() !== today.getFullYear() ? ` ${dt.getFullYear()}` : "");
};
const emailOf = (s) => { const m = /<([^>]+)>/.exec(s || ""); return m ? m[1] : (s || "").trim(); };
const nameOf = (s) => (s || "").replace(/<[^>]+>/, "").replace(/"/g, "").trim() || emailOf(s);

const ComposeDialog = ({ open, onClose, preset, onSent }) => {
  const [form, setForm] = useState({ to: "", cc: "", subject: "", body: "", in_reply_to: "" });
  const [files, setFiles] = useState([]);
  const [docRefs, setDocRefs] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({ to: "", cc: "", subject: "", body: "", in_reply_to: "", ...(preset || {}) });
      setFiles([]);
      setDocRefs([]);
      setShowCc(!!preset?.cc);
    }
  }, [open, preset]);

  const totalSize = files.reduce((s, f) => s + f.size, 0);

  const send = async () => {
    if (!form.to.trim()) { toast.error("Inserisci un destinatario"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      if (docRefs.length) fd.append("doc_refs", JSON.stringify(docRefs.map(({ type, id }) => ({ type, id }))));
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/webmail/send`, fd);
      toast.success("Email inviata");
      onClose();
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio fallito");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" data-testid="webmail-compose-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PenSquare className="w-5 h-5 text-[#8A1C2A]" />Scrivi email</DialogTitle></DialogHeader>
        <div className="space-y-2 overflow-y-auto flex-1 pr-1">
          <div className="flex items-center gap-2">
            <RecipientInput value={form.to} onChange={(v) => setForm((f) => ({ ...f, to: v }))} placeholder="A — destinatari separati da virgola" testId="compose-to" />
            {!showCc && <button className="text-xs text-stone-500 underline shrink-0" onClick={() => setShowCc(true)} data-testid="compose-show-cc">Cc</button>}
          </div>
          {showCc && <div className="flex"><RecipientInput value={form.cc} onChange={(v) => setForm((f) => ({ ...f, cc: v }))} placeholder="Cc" testId="compose-cc" /></div>}
          <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Oggetto" data-testid="compose-subject" />
          <Textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={12} placeholder="Scrivi il messaggio…" className="text-sm" data-testid="compose-body" />
          {(files.length > 0 || docRefs.length > 0) && (
            <div className="flex flex-wrap gap-1.5">
              {docRefs.map((d, i) => (
                <span key={`${d.type}-${d.id}`} className="inline-flex items-center gap-1.5 bg-[#8A1C2A]/5 border border-[#8A1C2A]/25 text-[#8A1C2A] rounded-full px-2.5 py-1 text-xs font-medium" data-testid={`compose-docref-${i}`}>
                  <FileText className="w-3 h-3" /> {d.label}
                  <button onClick={() => setDocRefs((ds) => ds.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button>
                </span>
              ))}
              {files.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 bg-stone-100 border border-stone-200 rounded-full px-2.5 py-1 text-xs">
                  <Paperclip className="w-3 h-3" /> {f.name} <span className="text-stone-400">{fmtSize(f.size)}</span>
                  <button onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}><X className="w-3 h-3 text-stone-500" /></button>
                </span>
              ))}
              {totalSize > 10 * 1048576 && <span className="text-xs text-red-600 font-semibold">Massimo 10 MB totali</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-stone-100 shrink-0">
          <Button onClick={send} disabled={sending || totalSize > 10 * 1048576} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="compose-send">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Invia
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="compose-attach">
            <Paperclip className="w-4 h-4 mr-2" /> Allega
          </Button>
          <Button variant="outline" onClick={() => setPickerOpen(true)} data-testid="compose-attach-doc">
            <FileText className="w-4 h-4 mr-2" /> Dal gestionale
          </Button>
          <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { setFiles((fs) => [...fs, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="compose-file-input" />
        </div>
        <DocPicker open={pickerOpen} onClose={() => setPickerOpen(false)} picked={docRefs}
          onPick={(r) => { setDocRefs((ds) => [...ds, r]); toast.success(`${r.label} allegato`); }} />
      </DialogContent>
    </Dialog>
  );
};

const MessageView = ({ msg, folderRole, onAction, folder }) => {
  const downloadAtt = async (a) => {
    try {
      const r = await axios.get(`${API}/webmail/messages/${msg.uid}/attachments/${a.idx}`, { params: { folder }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const el = document.createElement("a");
      el.href = url; el.download = a.filename; el.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore download allegato"); }
  };
  const srcDoc = msg.html
    ? `<base target="_blank"><style>body{font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#292524;margin:12px;word-break:break-word}img{max-width:100%;height:auto}</style>${msg.html}`
    : `<style>body{font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#292524;margin:12px;white-space:pre-wrap;word-break:break-word}</style>${(msg.text || "").replace(/</g, "&lt;")}`;
  return (
    <div className="flex flex-col h-full" data-testid="webmail-message-view">
      <div className="px-4 py-3 border-b border-stone-200 shrink-0">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-bold text-stone-900 leading-snug" data-testid="message-subject">{msg.subject}</h2>
          <span className="text-xs text-stone-400 whitespace-nowrap mt-0.5">{msg.date ? new Date(msg.date).toLocaleString("it-IT") : ""}</span>
        </div>
        <p className="text-sm text-stone-600 mt-1"><b>{nameOf(msg.from)}</b> <span className="text-stone-400">&lt;{emailOf(msg.from)}&gt;</span></p>
        <p className="text-xs text-stone-400">a {msg.to}{msg.cc ? ` · cc ${msg.cc}` : ""}</p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          <Button size="sm" variant="outline" onClick={() => onAction("reply")} data-testid="msg-reply"><Reply className="w-3.5 h-3.5 mr-1" />Rispondi</Button>
          <Button size="sm" variant="outline" onClick={() => onAction("replyAll")} data-testid="msg-reply-all"><ReplyAll className="w-3.5 h-3.5 mr-1" />A tutti</Button>
          <Button size="sm" variant="outline" onClick={() => onAction("forward")} data-testid="msg-forward"><Forward className="w-3.5 h-3.5 mr-1" />Inoltra</Button>
          <Button size="sm" variant="outline" onClick={() => onAction("unread")} data-testid="msg-unread"><MailIcon className="w-3.5 h-3.5 mr-1" />Non letto</Button>
          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 hover:border-red-200" onClick={() => onAction("delete")} data-testid="msg-delete"><Trash2 className="w-3.5 h-3.5 mr-1" />Elimina</Button>
        </div>
        {msg.attachments?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="message-attachments">
            {msg.attachments.map((a) => (
              <button key={a.idx} onClick={() => downloadAtt(a)} className="inline-flex items-center gap-1.5 bg-stone-100 hover:bg-stone-200 border border-stone-200 rounded-full px-2.5 py-1 text-xs" data-testid={`att-${a.idx}`}>
                <Download className="w-3 h-3" /> {a.filename} <span className="text-stone-400">{fmtSize(a.size)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <iframe title="email" sandbox="allow-popups" srcDoc={srcDoc} className="flex-1 w-full bg-white" data-testid="message-body-frame" />
    </div>
  );
};

export default function WebmailPage() {
  const [folders, setFolders] = useState(null);
  const [folder, setFolder] = useState("INBOX");
  const [list, setList] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState(null);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [error, setError] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composePreset, setComposePreset] = useState(null);

  const currentFolder = folders?.find((f) => f.id === folder);

  const loadFolders = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/webmail/folders`);
      setFolders(r.data);
      setNotConfigured(false);
      setError(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d === "not_configured") setNotConfigured(true);
      else setError(d || "Errore di connessione alla casella");
      setFolders([]);
    }
  }, []);

  const loadList = useCallback(async (f = folder, p = page, s = search) => {
    setLoadingList(true);
    try {
      const r = await axios.get(`${API}/webmail/messages`, { params: { folder: f, page: p, search: s } });
      setList(r.data);
      setError(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d === "not_configured") setNotConfigured(true);
      else setError(d || "Errore caricamento messaggi");
    } finally { setLoadingList(false); }
  }, [folder, page, search]);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { if (!notConfigured) loadList(folder, page, search); }, [folder, page, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const openMessage = async (m) => {
    setLoadingMsg(true);
    setSelected({ ...m, loading: true });
    try {
      const r = await axios.get(`${API}/webmail/messages/${m.uid}`, { params: { folder } });
      setSelected(r.data);
      setList((l) => l ? { ...l, messages: l.messages.map((x) => (x.uid === m.uid ? { ...x, seen: true } : x)) } : l);
      if (!m.seen) loadFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore apertura messaggio");
      setSelected(null);
    } finally { setLoadingMsg(false); }
  };

  const quote = (msg) => {
    const src = msg.text || (msg.html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ");
    const head = `\n\n\n--- Il ${msg.date ? new Date(msg.date).toLocaleString("it-IT") : ""}, ${nameOf(msg.from)} <${emailOf(msg.from)}> ha scritto:\n`;
    return head + (src || "").split("\n").map((l) => `> ${l}`).join("\n");
  };

  const action = async (kind) => {
    const msg = selected;
    if (!msg) return;
    if (kind === "reply" || kind === "replyAll" || kind === "forward") {
      const preset = { subject: (kind === "forward" ? "Fwd: " : "Re: ") + (msg.subject || "").replace(/^(Re|Fwd):\s*/i, ""), body: quote(msg), in_reply_to: msg.message_id || "" };
      if (kind === "reply") preset.to = emailOf(msg.from);
      if (kind === "replyAll") {
        preset.to = emailOf(msg.from);
        preset.cc = [msg.to, msg.cc].filter(Boolean).join(", ");
      }
      setComposePreset(preset);
      setComposeOpen(true);
      return;
    }
    if (kind === "unread") {
      try {
        await axios.post(`${API}/webmail/messages/${msg.uid}/flags`, { folder, seen: false });
        setList((l) => l ? { ...l, messages: l.messages.map((x) => (x.uid === msg.uid ? { ...x, seen: false } : x)) } : l);
        loadFolders();
        setSelected(null);
        toast.success("Segnato come non letto");
      } catch { toast.error("Errore"); }
      return;
    }
    if (kind === "delete") {
      if (!window.confirm("Eliminare questo messaggio?")) return;
      try {
        await axios.post(`${API}/webmail/messages/${msg.uid}/delete`, { folder });
        setSelected(null);
        toast.success("Messaggio eliminato");
        loadList();
        loadFolders();
      } catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
    }
  };

  if (notConfigured) {
    return (
      <div className="bg-white border border-stone-200 rounded-xl p-10 text-center max-w-xl mx-auto mt-8" data-testid="webmail-not-configured">
        <Inbox className="w-12 h-12 text-stone-300 mx-auto mb-3" />
        <h2 className="font-bold text-lg text-stone-900">Casella email non configurata</h2>
        <p className="text-sm text-stone-500 mt-1 mb-4">Per usare la Mail devi prima configurare la tua casella (server IMAP, utente e password) nelle Impostazioni email.</p>
        <p className="text-xs text-stone-400 flex items-center justify-center gap-1"><Settings className="w-3.5 h-3.5" /> Vai in Impostazioni → La mia casella email (SMTP/IMAP)</p>
      </div>
    );
  }

  const totalPages = list ? Math.max(1, Math.ceil(list.total / list.per_page)) : 1;

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] min-h-[480px]" data-testid="webmail-page">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <h1 className="text-xl font-bold text-stone-900 mr-2">Mail</h1>
        <Button onClick={() => { setComposePreset(null); setComposeOpen(true); }} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="webmail-compose-btn">
          <PenSquare className="w-4 h-4 mr-2" /> Scrivi
        </Button>
        <Button variant="outline" onClick={() => { loadFolders(); loadList(); }} data-testid="webmail-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loadingList ? "animate-spin" : ""}`} />
        </Button>
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); setSearch(searchInput.trim()); } }}
            placeholder="Cerca (oggetto, mittente)… Invio per cercare" className="pl-9" data-testid="webmail-search" />
        </div>
        {search && <button className="text-xs text-stone-500 underline" onClick={() => { setSearch(""); setSearchInput(""); setPage(1); }} data-testid="webmail-search-clear">annulla ricerca</button>}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2 mb-3" data-testid="webmail-error">{error}</div>}

      <div className="flex-1 flex gap-3 min-h-0">
        {/* Cartelle */}
        <div className="w-44 shrink-0 hidden md:flex flex-col gap-0.5 overflow-y-auto" data-testid="webmail-folders">
          {folders === null && <Loader2 className="w-5 h-5 animate-spin text-stone-300 mx-auto mt-6" />}
          {folders?.map((f) => {
            const Icon = ROLE_ICONS[f.role] || Folder;
            return (
              <button key={f.id} onClick={() => { setFolder(f.id); setPage(1); setSelected(null); }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left ${folder === f.id ? "bg-[#8A1C2A] text-white" : "text-stone-700 hover:bg-stone-100"}`}
                data-testid={`webmail-folder-${f.role || f.name}`}>
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate flex-1">{f.name}</span>
                {f.unseen > 0 && <span className={`text-[10px] font-bold px-1.5 rounded-full ${folder === f.id ? "bg-white/20" : "bg-[#8A1C2A] text-white"}`}>{f.unseen}</span>}
              </button>
            );
          })}
        </div>

        {/* Lista messaggi */}
        <div className={`bg-white border border-stone-200 rounded-xl flex flex-col min-h-0 w-full md:w-[360px] md:shrink-0 ${selected ? "hidden lg:flex" : "flex"}`} data-testid="webmail-list">
          <div className="md:hidden flex gap-1 p-2 border-b border-stone-100 overflow-x-auto">
            {folders?.map((f) => (
              <button key={f.id} onClick={() => { setFolder(f.id); setPage(1); }} className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap ${folder === f.id ? "bg-[#8A1C2A] text-white" : "bg-stone-100 text-stone-600"}`}>
                {f.name}{f.unseen > 0 ? ` (${f.unseen})` : ""}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-stone-100">
            {loadingList && !list && <div className="p-6 text-center"><Loader2 className="w-6 h-6 animate-spin text-stone-300 mx-auto" /></div>}
            {list?.messages.length === 0 && <p className="p-8 text-center text-sm text-stone-400" data-testid="webmail-empty">Nessun messaggio</p>}
            {list?.messages.map((m) => (
              <button key={m.uid} onClick={() => openMessage(m)}
                className={`w-full text-left px-3 py-2.5 hover:bg-stone-50 ${selected?.uid === m.uid ? "bg-stone-100" : ""} ${!m.seen ? "bg-[#FDF8F4]" : ""}`}
                data-testid={`webmail-msg-${m.uid}`}>
                <div className="flex items-center gap-2">
                  {!m.seen && <span className="w-2 h-2 rounded-full bg-[#8A1C2A] shrink-0" />}
                  <span className={`text-sm truncate flex-1 ${!m.seen ? "font-bold text-stone-900" : "text-stone-700"}`}>
                    {currentFolder?.role === "sent" ? `A: ${nameOf(m.to)}` : nameOf(m.from)}
                  </span>
                  {m.has_attachments && <Paperclip className="w-3 h-3 text-stone-400 shrink-0" />}
                  <span className="text-[11px] text-stone-400 shrink-0">{fmtDate(m.date)}</span>
                </div>
                <p className={`text-[13px] truncate ${!m.seen ? "font-semibold text-stone-800" : "text-stone-500"}`}>{m.subject}</p>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-t border-stone-100 text-xs text-stone-500 shrink-0">
            <span data-testid="webmail-total">{list ? `${list.total} messaggi` : ""}</span>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1 disabled:opacity-30" data-testid="webmail-prev"><ChevronLeft className="w-4 h-4" /></button>
              <span>{page}/{totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="p-1 disabled:opacity-30" data-testid="webmail-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Lettura */}
        <div className={`bg-white border border-stone-200 rounded-xl flex-1 min-w-0 min-h-0 ${selected ? "flex flex-col" : "hidden lg:flex items-center justify-center"}`} data-testid="webmail-reading-pane">
          {selected && (
            <button className="lg:hidden flex items-center gap-1 text-sm text-stone-500 px-3 pt-2" onClick={() => setSelected(null)} data-testid="webmail-back">
              <ArrowLeft className="w-4 h-4" /> Torna alla lista
            </button>
          )}
          {!selected && <div className="text-center text-stone-300"><MailOpen className="w-14 h-14 mx-auto mb-2" /><p className="text-sm">Seleziona un messaggio</p></div>}
          {selected?.loading || loadingMsg ? (
            selected && <div className="flex-1 flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-stone-300" /></div>
          ) : (
            selected && <MessageView msg={selected} folder={folder} folderRole={currentFolder?.role} onAction={action} />
          )}
        </div>
      </div>

      <ComposeDialog open={composeOpen} onClose={() => setComposeOpen(false)} preset={composePreset}
        onSent={() => { if (currentFolder?.role === "sent") loadList(); loadFolders(); }} />
    </div>
  );
}
